import * as React from 'react';
import { Formik, FormikErrors } from 'formik';
import { Button, Typography, TextField, styled } from '@mui/material';
import { useForgotPasswordMutation } from 'src/redux/services/auth';
import FidoLogo from 'src/frontend-common/components/Logo';
import ForgotPassword from 'src/frontend-common/components/Icons/ForgotPassword';
import Column from 'src/frontend-common/components/Column';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface FormValues {
  username: string;
}

const Container = styled('div')(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(5),
  width: '50%',
  alignSelf: 'center',
  [theme.breakpoints.up('xl')]: {
    maxWidth: '50%',
    padding: theme.spacing(15),
  },
}));

const Spacer = styled(Column)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 32,
  lineHeight: 1.2,
  color: theme.palette.primary.main,
  marginBottom: 20,
  textAlign: 'center',
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  lineHeight: 1.1,
  color: theme.palette.text.primary,
  textAlign: 'center',
}));

const CustomTextField = styled(TextField)(() => ({
  marginTop: 40,
  width: '100%',
}));

const CustomButton = styled(Button)(() => ({
  height: 56,
  marginTop: 40,
  width: '100%',
}));

interface PropTypes {
  onFormComplete: (username: string) => void;
}

const ForgotPasswordForm: React.FC<PropTypes> = ({ onFormComplete }) => {
  const [forgetPassword, { isLoading, isError }] = useForgotPasswordMutation();
  const navigate = useNavigate();
  return (
    <Formik
      initialValues={{
        username: '',
        __all: '',
      }}
      validateOnChange={true}
      validate={(values) => {
        const errors: FormikErrors<FormValues> = {};

        if (values.username.length === 0) {
          errors.username = 'This field is required';
        }

        return errors;
      }}
      onSubmit={async (values, { setErrors }) => {
        const result = await forgetPassword({
          username: values.username,
        });

        if ('error' in result && 'data' in result.error) {
          const violations = // @ts-ignore:next-line
            result.error?.data?.violations as FormikErrors<FormValues>;
          setErrors({ ...violations });
          return;
        }

        if ('data' in result && 'info' in result.data) {
          if (result.data.info === 'resend') {
            navigate('/auth/password-expired');
            return;
          }
        }

        onFormComplete(values.username);
      }}
    >
      {(formikProps) => {
        const {
          errors: { username: usernameError, __all: genericErrors },
          touched: { username: usernameTouched },
        } = formikProps;

        return (
          <Container>
            <div style={{ marginBottom: 50 }}>
              <FidoLogo />
            </div>
            <div>
              <div style={{ marginBottom: 24 }}>
                <ForgotPassword />
              </div>
              <Spacer centered>
                <Title style={{ fontSize: 40, fontWeight: 600, color: 'black' }}>Forgot password?</Title>
                <Subtitle>
                  <Typography
                    style={{
                      fontSize: 14,
                      fontWeight: 400,
                      color: '#616161',
                    }}
                  >
                    Lost your password? Please enter your username or email address.
                    <br />
                    You will receive a link to reset your password via email.
                  </Typography>
                </Subtitle>
              </Spacer>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  formikProps.handleSubmit();
                }}
              >
                <CustomTextField
                  variant="outlined"
                  label="Email address"
                  error={usernameTouched && !!usernameError}
                  helperText={usernameTouched && usernameError}
                  {...formikProps.getFieldProps('username')}
                />
              </form>

              {isError && (
                <Typography style={{ marginTop: 16 }} variant="caption" color="error">
                  {genericErrors}
                </Typography>
              )}

              <CustomButton
                variant="contained"
                color="primary"
                style={{ backgroundColor: 'black', color: 'white' }}
                disabled={isLoading}
                onClick={() => formikProps.submitForm()}
              >
                {isLoading ? 'Sending...' : 'Request password change'}
              </CustomButton>
              <div style={{ marginTop: 20, marginBottom: 20 }}>
                <span style={{ display: 'inline-block', marginRight: 5 }}>Back to</span>
                <div style={{ display: 'inline-block', cursor: 'pointer' }} onClick={() => navigate('/login')}>
                  <b> log in</b>
                  <ArrowForward style={{ float: 'right', cursor: 'pointer' }} />
                </div>
              </div>
            </div>
          </Container>
        );
      }}
    </Formik>
  );
};

export default ForgotPasswordForm;
